import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, XCircle, Building2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import AdminHeader from "@/components/AdminHeader";
import { toast } from "sonner";

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "paid", label: "Paid" },
  { key: "rejected", label: "Rejected" },
];

function formatDT(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
}

const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminWithdrawals() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, paid: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/withdrawals", { params: { status } });
      setItems(data.items || []);
      setCounts(data.counts || { pending: 0, paid: 0, rejected: 0 });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const decide = async (id, action) => {
    const note = action === "reject"
      ? window.prompt("Reason (shown to user):") || ""
      : window.prompt("Payment reference / UTR (optional):") || "";
    try {
      await api.post(`/admin/withdrawals/${id}/${action}`, { note });
      toast.success(action === "approve" ? "Marked as Paid" : "Withdrawal rejected & refunded");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen text-white" style={{ background: "#050506" }}>
      <AdminHeader title="Bank Withdrawals" />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-5">
        <Link to="/admin/dashboard" className="inline-flex items-center gap-1 text-emerald-400 text-sm font-semibold">
          <ArrowLeft size={14} /> Back to dashboard
        </Link>

        <section className="rounded-2xl border border-white/10 bg-zinc-950/60 overflow-hidden">
          <div className="p-4 border-b border-white/10 flex gap-2 flex-wrap">
            {TABS.map((t) => (
              <button
                key={t.key}
                data-testid={`wd-tab-${t.key}`}
                onClick={() => setStatus(t.key)}
                className={`text-xs uppercase tracking-widest font-semibold px-3 py-2 rounded-lg border transition-colors ${
                  status === t.key
                    ? "bg-emerald-400 text-black border-emerald-400"
                    : "border-white/10 text-white/80 hover:bg-white/5"
                }`}
              >
                {t.label} · <span className="font-mono">{counts[t.key] ?? 0}</span>
              </button>
            ))}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black/40 text-white/60 uppercase text-[11px] tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">User</th>
                  <th className="text-left px-4 py-3">Bank</th>
                  <th className="text-right px-4 py-3">Amount</th>
                  <th className="text-left px-4 py-3">Requested</th>
                  <th className="text-left px-4 py-3">Reviewed</th>
                  <th className="text-right px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody data-testid="admin-wd-table" className="divide-y divide-white/5">
                {loading ? (
                  <tr><td colSpan={6} className="text-center py-10 text-white/60">Loading…</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-10 text-white/60">No {status} withdrawals.</td></tr>
                ) : items.flatMap((w) => {
                  const expanded = openId === w.id;
                  const rows = [(
                    <tr key={w.id} className="hover:bg-white/5">
                      <td className="px-4 py-3">
                        <div className="font-semibold">{w.user_name}</div>
                        <div className="text-xs text-emerald-400 font-mono">{w.user_code}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs">{w.bank?.bank_name || "—"}</div>
                        <div className="text-xs text-white/60 font-mono">•••{(w.bank?.account_number || "").slice(-4)}</div>
                      </td>
                      <td className="px-4 py-3 text-right font-extrabold text-amber-300 tabular-nums">
                        {INR(w.amount)}
                      </td>
                      <td className="px-4 py-3 text-xs text-white/70">{formatDT(w.created_at)}</td>
                      <td className="px-4 py-3 text-xs text-white/70">{formatDT(w.reviewed_at)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          data-testid={`wd-toggle-${w.id}`}
                          onClick={() => setOpenId(expanded ? null : w.id)}
                          className="text-xs text-white/70 hover:text-white mr-3"
                        >
                          {expanded ? "Hide" : "Details"}
                        </button>
                        {status === "pending" ? (
                          <div className="inline-flex gap-2">
                            <button
                              data-testid={`wd-approve-${w.id}`}
                              onClick={() => decide(w.id, "approve")}
                              className="inline-flex items-center gap-1 text-xs bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-3 py-1.5 rounded-lg"
                            >
                              <CheckCircle2 size={13} /> Mark Paid
                            </button>
                            <button
                              data-testid={`wd-reject-${w.id}`}
                              onClick={() => decide(w.id, "reject")}
                              className="inline-flex items-center gap-1 text-xs border border-red-500/50 text-red-400 hover:bg-red-500/20 px-3 py-1.5 rounded-lg"
                            >
                              <XCircle size={13} /> Reject
                            </button>
                          </div>
                        ) : status === "paid" ? (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-400 font-semibold">
                            <CheckCircle2 size={13} /> Paid
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-red-400 font-semibold">
                            <XCircle size={13} /> Rejected
                          </span>
                        )}
                      </td>
                    </tr>
                  )];
                  if (expanded) {
                    rows.push(
                      <tr key={`${w.id}-detail`} className="bg-black/40">
                        <td colSpan={6} className="px-4 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                            <div><b className="text-white/60">Holder:</b> {w.bank?.account_holder || "—"}</div>
                            <div><b className="text-white/60">Account:</b> {w.bank?.account_number || "—"}</div>
                            <div><b className="text-white/60">IFSC:</b> {w.bank?.ifsc || "—"}</div>
                            {w.admin_note && <div className="md:col-span-3"><b className="text-white/60">Admin note:</b> {w.admin_note}</div>}
                          </div>
                        </td>
                      </tr>
                    );
                  }
                  return rows;
                })}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
